import React from "react";
import PropTypes from "prop-types";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Stack,
  Divider,
  Button,
  CircularProgress,
  Fade,
} from "@mui/material";
import { FormattedMessage } from "react-intl";
import { ArrowBackIcon } from "../../../../components/icons/icons-svg/arrow-back-icon";
import { QuestionMarkTooltip } from "../../../../components/common/tooltip/question-mark-tooltip";

export const TeachingDialogLayout = ({
  isOpen,
  isMobile,
  onClose,
  title,
  subtitle,
  showTooltip = false,
  tooltipId,
  children,
  actions,
  showSuccess = false,
  successTitle,
  successMessage,
}) => {
  return (
    <Fade in={isOpen} timeout={300}>
      <Box sx={{ 
        position: 'relative', 
        minHeight: isMobile ? '100vh' : 'auto',
        display: 'flex',
        flexDirection: 'column',
        height: isMobile ? '100vh' : 'auto',
        maxHeight: isMobile ? '100vh' : '90vh',
      }}>
        {/* Success State */}
        {showSuccess && (
          <Fade in={showSuccess} timeout={500}>
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(76, 175, 80, 0.95)',
                color: 'white',
                zIndex: 10,
              }}
            >
              <CircularProgress size={60} sx={{ color: 'white', mb: 2 }} />
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                {successTitle || <FormattedMessage id="teacher.success.title" defaultMessage="Success!" />}
              </Typography>
              <Typography variant="body1">
                {successMessage || <FormattedMessage id="teacher.success.message" defaultMessage="Operation completed successfully!" />}
              </Typography>
            </Box>
          </Fade>
        )}

        <Card sx={{ 
          boxShadow: 'none', 
          borderRadius: 0,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflow: 'hidden',
        }}>
          <CardContent sx={{ 
            p: { xs: 3, md: 4 },
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            overflow: 'hidden',
          }}>
            {/* Header - Fixed */}
            <Box sx={{ mb: 3, flexShrink: 0 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Stack direction="row" alignItems="center" spacing={2}>
                  {isMobile && (
                    <Button
                      onClick={onClose}
                      sx={{ minWidth: 'auto', p: 1 }}
                    >
                      <ArrowBackIcon color="#383838" width={20} height={20} />
                    </Button>
                  )}
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                      {title}
                    </Typography>
                    {subtitle && (
                      <Chip 
                        label={subtitle}
                        color="primary" 
                        variant="outlined"
                        size="small"
                      />
                    )}
                  </Box>
                </Stack>
                {showTooltip && tooltipId && (
                  <QuestionMarkTooltip tooltipId={tooltipId} />
                )}
              </Stack>
            </Box>

            <Divider sx={{ mb: 3, flexShrink: 0 }} />

            {/* Content - Scrollable */}
            <Box sx={{ 
              flex: 1,
              overflow: 'auto',
              minHeight: 0, // Important for flexbox scrolling
            }}>
              {children}
            </Box>

            {/* Actions - Fixed at bottom */}
            {actions && (
              <Box sx={{ 
                mt: 4, 
                display: 'flex', 
                justifyContent: 'flex-end', 
                gap: 2,
                flexShrink: 0,
                pt: 2,
                borderTop: '1px solid',
                borderColor: 'divider',
              }}>
                {actions}
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>
    </Fade>
  );
};

TeachingDialogLayout.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  isMobile: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.node,
  showTooltip: PropTypes.bool,
  tooltipId: PropTypes.string,
  children: PropTypes.node.isRequired,
  actions: PropTypes.node,
  showSuccess: PropTypes.bool,
  successTitle: PropTypes.string,
  successMessage: PropTypes.string,
};
